// src/utils/tokenExpirationManager.js
// *******************************************************************************
// Token Expiration Manager - Gerencia logout automático baseado na expiração do JWT
// *******************************************************************************

import { getStorageAuthItem, removeStorageAuthItem, setStorageAuthItem } from 'stores/auth';

let expirationTimer = null;

/**
 * Decodifica JWT e retorna o payload
 * @param {string} token - JWT token (com ou sem Bearer)
 * @returns {object|null} - Payload decodificado ou null se falhar
 */
function decodeJWT(token) {
  try {
    const cleanToken = token.replace('Bearer ', '').trim();
    const base64Url = cleanToken.split('.')[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.warn('[TokenExpiration] Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Agenda o logout automático baseado na expiração do token
 * @param {string} token - JWT token
 * @param {function} onExpire - Callback a ser executado quando o token expirar
 */
export function scheduleAutoLogout(token, onExpire) {
  // Limpa timer anterior se existir
  clearAutoLogout();

  if (!token) {
    console.warn('[TokenExpiration] No token provided');
    return;
  }

  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    console.warn('[TokenExpiration] Invalid token or missing exp claim');
    return;
  }

  // exp vem em segundos, Date.now() em milissegundos
  const expirationTime = payload.exp * 1000;
  const now = Date.now();
  const timeUntilExpiration = expirationTime - now;

  // Salva no localStorage para recuperar após reload
  try {
    setStorageAuthItem('token_exp_time', expirationTime.toString());
  } catch (error) {
    console.warn('[TokenExpiration] Failed to save expiration to localStorage:', error);
  }

  // Se já expirou, executa imediatamente
  if (timeUntilExpiration <= 0) {
    onExpire();
    return;
  }

  // Agenda o logout automático
  // Subtrai 1 segundo para garantir que expira antes de qualquer request
  const safeTimeUntilExpiration = Math.max(0, timeUntilExpiration - 1000);

  expirationTimer = setTimeout(() => {
    onExpire();
  }, safeTimeUntilExpiration);
}

/**
 * Cancela o logout agendado e limpa dados do localStorage
 */
export function clearAutoLogout() {
  if (expirationTimer) {
    clearTimeout(expirationTimer);
    expirationTimer = null;
  }

  try {
    removeStorageAuthItem('token_exp_time');
  } catch (error) {
    console.warn('[TokenExpiration] Failed to remove expiration from localStorage:', error);
  }
}

/**
 * Verifica se há um token expirado no localStorage e agenda logout se necessário
 * Útil para re-agendar o timeout após reload da página
 * @param {function} onExpire - Callback a ser executado quando o token expirar
 * @returns {boolean} - true se o token já estava expirado, false caso contrário
 */
export function checkStoredTokenExpiration(onExpire) {
  try {
    const storedExp = getStorageAuthItem('token_exp_time');
    if (!storedExp) return false;

    const expirationTime = Number(storedExp);
    if (!expirationTime || isNaN(expirationTime)) {
      removeStorageAuthItem('token_exp_time');
      return false;
    }

    const now = Date.now();
    const timeUntilExpiration = expirationTime - now;

    if (timeUntilExpiration <= 0) {
      onExpire();
      return true;
    }

    // Clear any existing timer before setting a new one
    // CRITICAL: Without this, scheduleAutoLogout's timer becomes orphaned
    // and fires at the original login time, ignoring token renewals
    if (expirationTimer) {
      clearTimeout(expirationTimer);
      expirationTimer = null;
    }

    // Re-agenda o timeout com o tempo restante
    const safeTimeUntilExpiration = Math.max(0, timeUntilExpiration - 1000);
    expirationTimer = setTimeout(() => {
      onExpire();
    }, safeTimeUntilExpiration);

    return false;
  } catch (error) {
    console.warn('[TokenExpiration] Error checking stored expiration:', error);
    return false;
  }
}
